import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getFidelisation } from '../../services/rapportService';
import {
  NdoePage, NdoePageHeader, NdoeActionButton, NdoeResponsiveTable,
  NdoeStats, NdoeStatCard, NdoeError, NdoeLoading,
} from '../../components/ndoe';
import { money, pct } from './format';
import './rapports.css';

const MOIS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const moisLabel = (m) => {
  const [y, mm] = String(m || '').split('-');
  const idx = Number(mm) - 1;
  return MOIS_FR[idx] ? `${MOIS_FR[idx]} ${y}` : m;
};

// Barre partagée nouvelles (bleu nuit) / fidèles (or) — proportions du CA du mois.
// Mois sans CA : barre neutre vide (pas une barre « 100 % nouvelles » mensongère).
// Les deux pourcentages du tooltip sont complémentaires (jamais 49 % + 52 %).
const SplitBar = ({ nouvelles, fideles }) => {
  const total = (Number(nouvelles) || 0) + (Number(fideles) || 0);
  if (total <= 0) return <div className="fd-split fd-split--vide" title="Aucun CA ce mois-ci" />;
  const pf = (fideles / total) * 100;
  const pfAff = Math.round(pf);
  return (
    <div className="fd-split" title={`Fidèles ${pfAff} % · Nouvelles ${100 - pfAff} %`}>
      <i className="fd-split__fideles" style={{ width: `${pf}%` }} />
    </div>
  );
};

// Barre simple de pourcentage (cohortes).
const PctBar = ({ value }) => (
  <div className="anc-partbar" title={`${value} %`}>
    <div className="anc-partbar__track">
      <i style={{ width: `${Math.max(2, Math.min(100, Number(value) || 0))}%` }} />
    </div>
    <span className="num">{value} %</span>
  </div>
);

export default function Fidelisation() {
  const user = useSelector((s) => s.user?.currentUser);
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';
  const navigate = useNavigate();

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getFidelisation();
      setPayload(data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Impossible de charger le rapport de fidélisation.');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const k = payload?.kpis || {};
  const serie = payload?.serie || [];
  const cohortes = payload?.cohortes || [];
  const retourJ = payload?.seuils?.retour_rapide_jours || 90;

  const colsSerie = [
    { title: 'Mois', dataIndex: 'mois', key: 'mois', render: (v) => <b className="fd-mois">{moisLabel(v)}</b> },
    { title: 'Nouvelles vs fidèles', key: 'split', render: (t, r) => <SplitBar nouvelles={r.ca_nouvelles} fideles={r.ca_fideles} /> },
    { title: 'CA fidèles', dataIndex: 'ca_fideles', key: 'caf', align: 'right', render: (v) => <b className="num fd-or">{money(v)}</b> },
    { title: 'CA nouvelles', dataIndex: 'ca_nouvelles', key: 'can', align: 'right', render: (v) => <span className="num">{money(v)}</span> },
    { title: 'CA total', dataIndex: 'ca_total', key: 'cat', align: 'right', render: (v) => <span className="num">{money(v)}</span> },
    { title: 'Part fidèles', dataIndex: 'part_fideles_pct', key: 'pf', align: 'right', render: (v) => <span className="num">{pct(v)}</span> },
    { title: 'Clientes (dont nouvelles)', key: 'cli', align: 'right', render: (t, r) => <span className="num">{r.actives} ({r.nouvelles})</span> },
  ];

  const colsCohortes = [
    { title: '1er achat en', dataIndex: 'cohorte', key: 'c', render: (v) => <b className="fd-mois">{moisLabel(v)}</b> },
    { title: 'Clientes', dataIndex: 'clientes', key: 'n', align: 'right', render: (v) => <span className="num">{v}</span> },
    { title: 'Revenues acheter', key: 'rev', render: (t, r) => <PctBar value={r.pct_revenues} /> },
    { title: `Dont sous ${retourJ} j`, dataIndex: 'pct_90j', key: 'r90', align: 'right', render: (v) => <span className="num">{pct(v)}</span> },
  ];

  if (!isAdmin) {
    return (
      <NdoePage>
        <NdoePageHeader eyebrow="Rapports" title="Fidélisation & rétention" />
        <NdoeError title="Accès réservé" text="Ce rapport est réservé à l'administration." />
      </NdoePage>
    );
  }

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Rapports"
        title="Fidélisation & rétention"
        subtitle="Est-ce que vos clientes reviennent — et combien pèse la fidélité dans votre chiffre d'affaires, mois par mois."
        actions={<NdoeActionButton variant="secondary" onClick={() => navigate('/rapports/relance')}>Qui relancer →</NdoeActionButton>}
      />

      {error ? <NdoeError title="Erreur" text={error} onRetry={load} /> : loading ? <NdoeLoading rows={5} /> : (
        <>
          <NdoeStats>
            <NdoeStatCard label="Taux de ré-achat (historique)" value={pct(k.taux_reachat_pct)} gold />
            <NdoeStatCard label="Délai 1er → 2e achat" value={k.delai_1er_2e_moyen_j == null ? '—' : `${k.delai_1er_2e_moyen_j} j`} />
            <NdoeStatCard label="Délai moyen entre achats" value={k.delai_entre_achats_moyen_j == null ? '—' : `${k.delai_entre_achats_moyen_j} j`} />
            <NdoeStatCard label="Panier moyen / achat" value={money(k.panier_moyen_par_achat)} />
          </NdoeStats>

          <div className="ndoe-card fd-block">
            <div className="ndoe-section__title">CA par mois — nouvelles vs fidèles</div>
            <p className="fd-hint">
              <i className="fd-dot fd-dot--or" /> fidèles (déjà clientes avant le mois) ·{' '}
              <i className="fd-dot fd-dot--nuit" /> nouvelles (1er achat ce mois-là)
            </p>
            <NdoeResponsiveTable
              data={serie}
              columns={colsSerie}
              rowKey="mois"
              pagination={false}
              scroll={{ x: 'max-content' }}
              empty={{ icon: '📈', title: 'Aucune vente' }}
            />
          </div>

          <div className="ndoe-card fd-block">
            <div className="ndoe-section__title">Cohortes — reviennent-elles après le 1er achat ?</div>
            <p className="fd-hint">Chaque ligne = les clientes dont le 1er achat date de ce mois, et la part d'entre elles revenue acheter au moins une 2e fois.</p>
            <NdoeResponsiveTable
              data={cohortes}
              columns={colsCohortes}
              rowKey="cohorte"
              pagination={false}
              scroll={{ x: 'max-content' }}
              empty={{ icon: '🌱', title: 'Aucune cohorte' }}
            />
          </div>

          <p className="fd-note">
            {k.acheteuses} acheteuses sur l'historique · {k.revenues} revenues au moins une 2e fois.
            Les cohortes récentes n'ont pas fini leur course : leur % montera encore.
            Pour agir sur les clientes silencieuses, ouvrez « Qui relancer ».
          </p>
        </>
      )}
    </NdoePage>
  );
}
